/* eslint-disable class-methods-use-this */
import { WebViewerInstance, Core } from '@pdftron/webviewer';
import {
  AllDocumentTypes,
  AnnotationCustomData,
  Difference as DifferenceType,
  DifferenceTypeNames,
  GVAnnotationType,
  DocumentTypes,
  DocumentType,
  FormattingDifferences,
  MarkupZoneCustomData,
} from 'types';

import GVAnnotationMixin from 'pdftron/docManager/AnnotationTools/utils/GVAnnotationMixin';
import Difference from 'pdftron/docManager/Difference';
import Page from './Page';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import { getShowDifferenceBoxes, getShowOverlay } from 'store';
import store from 'store/store';
import Utils from 'components/PDFViewer/Utils';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { DEFAULT_DIFFERENCE_COLOR } from 'pdftron/docManager/differenceColors';
import { ColorId, colorIdToHex } from '@verify/shared-utils';
import { differenceHighlightColors } from 'pdftron/docManager/differenceColors';
import { getBarcodeGradeColor } from 'pdftron/utils/barcodeGradeColors';
import { PREP_TOOL_LABEL, PREP_TOOL_ANNOTATION } from 'pdftron/docManager/AnnotationTools/utils/annotationConstants';
import drawMarkupLabel from 'pdftron/docManager/AnnotationTools/utils/drawMarkupLabel';
import { getPageRotation } from 'pdftron/docManager/AnnotationTools/utils/labelPositionUtils';
import { getCachedFlagValue } from 'utils/getFlagValue/getFlagValue';
import { redrawPages } from 'pdftron/utils/renderUtils';

class Annotation {
  instance: WebViewerInstance;

  documentType: AllDocumentTypes;

  constructor(instance: WebViewerInstance, documentType: AllDocumentTypes) {
    this.instance = instance;
    this.documentType = documentType;
  }

  get page() {
    return new Page(this.instance, this.documentType);
  }

  get annotationManager() {
    return this.instance.Core.documentViewer.getAnnotationManager();
  }

  removeHighContrasts() {
    const overlays = this.getAnnotationList(
      (annot: Core.Annotations.Annotation) => annot.getCustomData('highContrast') === 'true',
    );
    if (overlays.length) {
      this.annotationManager.deleteAnnotation(overlays, { imported: true, force: true });
    }
  }

  /**
   * True when a high-contrast overlay already exists on the given page whose referenced annotations
   * and visibility flags match what we'd draw now. Lets the lazy on-scroll path skip already-drawn
   * pages without any refresh churn. Marker-flag nuances are intentionally ignored here — a stale
   * overlay is corrected by the normal delete/recreate path when the difference is (re)focused.
   */
  hasHighContrastOverlayForPage(pageNumber: number, pageAnnotations: Core.Annotations.Annotation[]): boolean {
    const refAnnotationIds = pageAnnotations.map((annot) => annot.Id).join(',');
    const showDifferenceBoxes = getShowDifferenceBoxes(store.getState());
    const showOverlay = getShowOverlay(store.getState());
    return (
      this.getAnnotationList(
        (annot: Core.Annotations.Annotation) =>
          annot.getCustomData('highContrast') === 'true' &&
          annot.PageNumber === pageNumber &&
          annot.getCustomData(AnnotationCustomData.refAnnotationIds) === refAnnotationIds &&
          (annot.getCustomData(AnnotationCustomData.noDiffBox) === 'true') === !showDifferenceBoxes &&
          (annot.getCustomData(AnnotationCustomData.hiddenOverlay) === 'true') === !showOverlay,
      ).length > 0
    );
  }

  handleOverlayVisibilityChange() {
    const showOverlay = getShowOverlay(store.getState());
    const overlays = this.getAnnotationList(
      (annot: Core.Annotations.Annotation) => annot.getCustomData('highContrast') === 'true',
    );
    showOverlay ? this.annotationManager.showAnnotations(overlays) : this.annotationManager.hideAnnotations(overlays);
  }

  /**
   * Draws the dimming high-contrast overlay for a group of differences.
   *
   * @param options.pages     When provided, only draw overlays for these page numbers. Used for
   *                          lazy per-visible-page drawing so a group spanning many pages doesn't
   *                          create and refresh an overlay on every page up front.
   * @param options.skipIfPresent When true, skip a page entirely (no refresh or z-order churn) if a
   *                          matching overlay already exists. Used by the on-scroll lazy path.
   */
  highContrastAnnotations(differences: DifferenceType[], options?: { pages?: number[]; skipIfPresent?: boolean }) {
    const annotationsByPage: Array<Core.Annotations.Annotation[]> = [];
    const isDeletion = Difference.requiresDeletionMarker(differences);
    const isBarcode = Difference.isBarcode(differences);
    const isUnmatchedAnnotation = Difference.isUnmatchedAnnotation(differences);
    const showDifferenceBoxes = getShowDifferenceBoxes(store.getState());
    const showOverlay = getShowOverlay(store.getState());
    const readingOrderPadding = Utils.annotationOptions.unselectedRectangeGrowthValue;
    const readingOrderUxV2 = getCachedFlagValue('reading_order_ux_v2');
    // to identify high contrast annotations when we need to restore them, we identify them by groupId for groups or Id for non-groups

    // Extract barcode grade and passingStatus for color selection
    let barcodeGrade: string | undefined;
    let barcodePassingStatus: string | undefined;
    if (isBarcode) {
      const barcodeDiff = differences[0] as DifferenceType;
      const gradeMetadata = barcodeDiff?.barcodeDetails?.barcodeMetadata?.find((m) => m.key === 'grade');
      barcodeGrade = gradeMetadata?.value;
      barcodePassingStatus = gradeMetadata?.passingStatus;
    }

    const differenceLookup = new Map<string, DifferenceType>();
    differences.forEach((difference: DifferenceType) => {
      differenceLookup.set(difference.id, difference);
      const annotation = this.getAnnotationById(difference.id);
      if (annotation) {
        // Skip crop and polygon zones - they have their own overlay drawing system
        const isCropZone = annotation.getCustomData('cropZoneAnnotation') === 'true';
        const isPolygonZone = annotation.getCustomData('polygonAnnotation') === 'true';
        if (isCropZone || isPolygonZone) {
          return;
        }
        // Color points are drawn only on target; skip source overlay (avoids duplicate / corner artifact)
        if (difference.type === DifferenceTypeNames.Color && this.documentType === DocumentTypes.source) {
          return;
        }

        const pageNumber = annotation?.PageNumber;
        if (!annotationsByPage[pageNumber]) annotationsByPage[pageNumber] = [];
        annotationsByPage[pageNumber]?.push(annotation);
      }
    });

    annotationsByPage.forEach((pageAnnotations: Core.Annotations.Annotation[]) => {
      const pageNumber = pageAnnotations[0].PageNumber;

      // Lazy scoping: only draw the requested pages (e.g. the focused/visible ones).
      if (options?.pages && !options.pages.includes(pageNumber)) return;
      // On-scroll path: if this page already has a matching overlay, skip it entirely so we don't
      // re-run redrawPages/bring-to-front on every scroll tick.
      if (options?.skipIfPresent && this.hasHighContrastOverlayForPage(pageNumber, pageAnnotations)) return;

      const pageSize = this.page.getPageSize(pageNumber);

      type OverlayBox = {
        height: number;
        width: number;
        x: number;
        y: number;
        refAnnotationId: string;
        textHighlights?: string;
        label?: 'before' | 'after';
        strokeColor?: string;
        labelColor?: string;
      };

      const overlayBoxes: OverlayBox[] = pageAnnotations.map((annotation) => ({
        height: annotation.Height,
        width: annotation.Width,
        x: annotation.X,
        y: annotation.Y,
        refAnnotationId: annotation.Id,
        textHighlights: annotation.getCustomData(AnnotationCustomData.textHighlights),
      }));

      if (readingOrderUxV2) {
        const readingOrderBoxes = pageAnnotations.flatMap((annotation) => {
          const difference = differenceLookup.get(annotation.Id);
          if (!difference || difference.type !== DifferenceTypeNames.ReadingOrder) {
            return [];
          }

          const documentData = this.documentType === DocumentTypes.source ? difference.source : difference.target;

          if (!documentData) return [];

          const candidateLocations = [
            { location: documentData.previousTextLocation, label: 'before' },
            { location: documentData.nextTextLocation, label: 'after' },
          ];

          const readingOrderColor =
            differenceHighlightColors[DifferenceTypeNames.ReadingOrder] || DEFAULT_DIFFERENCE_COLOR;

          return candidateLocations
            .map(({ location, label }) => {
              if (!location || location.pageNumber !== annotation.PageNumber) {
                return null;
              }

              const [rectX = 0, rectY = 0, rectWidth = 0, rectHeight = 0] = location.rect || [];
              return {
                height: rectHeight + 2 * readingOrderPadding,
                width: rectWidth + 2 * readingOrderPadding,
                x: rectX - readingOrderPadding,
                y: rectY - readingOrderPadding,
                refAnnotationId: annotation.Id,
                textHighlights: undefined,
                label,
                strokeColor: readingOrderColor,
                labelColor: readingOrderColor,
              } as OverlayBox | null;
            })
            .filter((box): box is OverlayBox => Boolean(box));
        });

        overlayBoxes.push(...readingOrderBoxes);
      }

      const refAnnotationIds = pageAnnotations.map((annot) => annot.Id);
      const overlayAnnotation = new (GVAnnotationMixin(this.instance.Core.Annotations.RectangleAnnotation))();
      overlayAnnotation.setCustomData(AnnotationCustomData.highContrast, 'true');
      overlayAnnotation.setCustomData(AnnotationCustomData.refAnnotationIds, refAnnotationIds.join(','));
      overlayAnnotation.NoResize = true;
      overlayAnnotation.ReadOnly = true;
      if (!showDifferenceBoxes) overlayAnnotation.setCustomData(AnnotationCustomData.noDiffBox, 'true');
      if (!showOverlay) overlayAnnotation.setCustomData(AnnotationCustomData.hiddenOverlay, 'true');

      overlayAnnotation.setCustomData('textFragment', overlayBoxes[0]?.textHighlights || '');

      const embeddedImageDiff = differences.find((diff) => {
        interface EmbeddedImageDetails {
          matchDetails: Array<{
            confidenceLevel: string;
            confidence: number;
          }>;
        }

        if (diff.type !== DifferenceTypeNames.EmbeddedImage || !('embeddedImageDetails' in diff)) {
          return false;
        }
        const details = diff.embeddedImageDetails as EmbeddedImageDetails;
        const hasNoMatch = details?.matchDetails?.[0]?.confidenceLevel === 'None';

        return hasNoMatch;
      });
      const embeddedImageDeletionTarget = embeddedImageDiff
        ? Difference.getEmbeddedImageDeletionTarget(embeddedImageDiff as DifferenceType)
        : null;

      const requiresDeletionMarker = Difference.requiresDeletionMarker(differences);
      const isInsertion = differences.some((diff) => diff.type === DifferenceTypeNames.Insertion);
      const shouldShowMarker =
        (requiresDeletionMarker && this.documentType === DocumentTypes.target && !embeddedImageDeletionTarget) ||
        (isInsertion && this.documentType === DocumentTypes.source) ||
        embeddedImageDeletionTarget === this.documentType;

      // setX and setY must happen before the draw function, otherwise the marker won't be able to move
      if (shouldShowMarker) {
        const annotationX = overlayBoxes[0]?.x || 0;
        const annotationY = overlayBoxes[0]?.y || 0;
        overlayAnnotation.setX(annotationX);
        overlayAnnotation.setY(annotationY);
        overlayAnnotation.setWidth(30);
        overlayAnnotation.setHeight(30);
      }

      overlayAnnotation.draw = (ctx: CanvasRenderingContext2D) => {
        const annotationIsDeletion = overlayAnnotation.getCustomData('isDeletion') === 'true';
        const isDeletionMarkerAnnotation =
          overlayAnnotation.getCustomData(AnnotationCustomData.deletionMarker) === 'true';
        const isExplicitlyNoMarker = overlayAnnotation.getCustomData('noMarker') === 'true';
        const shouldDrawMarker = (annotationIsDeletion || isDeletionMarkerAnnotation) && !isExplicitlyNoMarker;

        if (shouldDrawMarker) {
          // Draw missing content marker (deletion on target, insertion on source)

          // Ensure marker flags are set (keep deletionMarker for backward compatibility)
          overlayAnnotation.setCustomData('isDeletion', 'true');
          overlayAnnotation.setCustomData(AnnotationCustomData.deletionMarker, 'true');
          overlayAnnotation.ReadOnly = false;

          // Use same color for both insertions and deletions
          const markerColor = '#cd3434';

          overlayBoxes.forEach((boxSettings) => {
            if (boxSettings) {
              ctx.globalAlpha = 1;
              ctx.lineWidth = 1;
              // If the referenced annotation is selected for resync, reflect that with blue overlay stroke color
              const refAnnot = this.annotationManager.getAnnotationById(boxSettings.refAnnotationId);
              const isResyncSelected = refAnnot?.getCustomData('resyncSelected') === 'true';
              ctx.strokeStyle = isResyncSelected
                ? '#02A4B6'
                : showDifferenceBoxes
                  ? markerColor
                  : 'rgba(255, 255, 255, 0.01)';
              /**
               * Creating a new boxSettings coordinates based on the SDK
               * and drawing an X in Canvas way
               */
              const newBoxSettingsX = overlayAnnotation.X + overlayAnnotation.Width / 2;
              const newBoxSettingsY = overlayAnnotation.Y + overlayAnnotation.Height / 2;
              ctx.beginPath();
              ctx.moveTo(newBoxSettingsX - 10, newBoxSettingsY - 10);
              ctx.lineTo(newBoxSettingsX + 10, newBoxSettingsY + 10);
              ctx.moveTo(newBoxSettingsX + 10, newBoxSettingsY - 10);
              ctx.lineTo(newBoxSettingsX - 10, newBoxSettingsY + 10);
              ctx.stroke();
              ctx.closePath();
            }
          });
        } else {
          // Draw regular overlay
          ctx.save(); // Save canvas state BEFORE modifying it
          ctx.globalAlpha = 0.6;
          ctx.fillStyle = showOverlay ? '#404040' : 'rgba(255, 255, 255, 0.01)';
          ctx.fillRect(0, 0, pageSize.width, pageSize.height);
          overlayBoxes.forEach((boxSettings) => {
            if (!isBarcode || (isBarcode && this.documentType === DocumentTypes.target)) {
              if (boxSettings) {
                ctx.clearRect(boxSettings.x, boxSettings.y, boxSettings.width, boxSettings.height);
                ctx.globalAlpha = 1;
                // If the referenced annotation is selected for resync, reflect that with blue overlay stroke color
                const refAnnot = this.annotationManager.getAnnotationById(boxSettings.refAnnotationId);
                const isResyncSelected = refAnnot?.getCustomData('resyncSelected') === 'true';

                const state = store.getState();

                // Determine stroke color - use grade color for barcodes
                let diffBoxColor = '#cd3434'; // default red
                if (isBarcode) {
                  diffBoxColor = getBarcodeGradeColor(barcodeGrade, barcodePassingStatus);
                }

                let strokeStyle = isResyncSelected
                  ? '#02A4B6'
                  : showDifferenceBoxes
                    ? diffBoxColor
                    : 'rgba(255, 255, 255, 0.01)';
                if (boxSettings.strokeColor && showDifferenceBoxes && !isResyncSelected) {
                  strokeStyle = boxSettings.strokeColor;
                }
                ctx.strokeStyle = strokeStyle;
                ctx.lineWidth = 1;

                const selectedHighlightColor = state.inspection.settings.highlightColor;
                // since we clear the rectangle, we need to redraw any text fragments inside.
                const skipTextHighlights =
                  isUnmatchedAnnotation ||
                  (isDeletion && this.documentType === DocumentTypes.target) ||
                  (isInsertion && this.documentType === DocumentTypes.source);
                if (!skipTextHighlights && state.app.showTextHighlights && boxSettings.textHighlights) {
                  const differenceType = differences[0].type;
                  let TEXT_HIGHLIGHT_FILL_STYLE = differenceHighlightColors[differenceType] || DEFAULT_DIFFERENCE_COLOR;
                  if (selectedHighlightColor && selectedHighlightColor !== ColorId.MULTICOLOR) {
                    TEXT_HIGHLIGHT_FILL_STYLE =
                      colorIdToHex[selectedHighlightColor as Exclude<ColorId, ColorId.MULTICOLOR>] ||
                      DEFAULT_DIFFERENCE_COLOR;
                  }
                  // If the referenced annotation is selected for resync, use resync blue for text highlights
                  if (isResyncSelected) {
                    TEXT_HIGHLIGHT_FILL_STYLE = '#00E4F5';
                  }
                  const textHighlightData = JSON.parse(boxSettings.textHighlights);

                  ctx.fillStyle = TEXT_HIGHLIGHT_FILL_STYLE;
                  ctx.globalAlpha = differenceType in FormattingDifferences ? 0.15 : 0.2; // the pink is 15% and the other colors are 20%

                  ctx.globalCompositeOperation = 'destination-over';
                  textHighlightData.forEach((textHighlight: { rect: [number, number, number, number] }) => {
                    const fragmentCoordinates = textHighlight.rect;
                    const x = fragmentCoordinates[0] - Utils.annotationOptions.unselectedRectangeGrowthValue;
                    const y = fragmentCoordinates[1] - Utils.annotationOptions.unselectedRectangeGrowthValue;
                    const width = fragmentCoordinates[2] + 2 * Utils.annotationOptions.unselectedRectangeGrowthValue;
                    const height = fragmentCoordinates[3] + 2 * Utils.annotationOptions.unselectedRectangeGrowthValue;
                    const textHighlightRectangle = new Path2D();
                    textHighlightRectangle.rect(x, y, width, height);
                    ctx.fill(textHighlightRectangle);
                  });
                  ctx.globalAlpha = 1;
                }

                if (Difference.isGraphicOrEmbeddedImage(differences) && this.documentType === DocumentTypes.source) {
                  // for text identifying annotation ex. Inspection Zone 1
                  ctx.textAlign = 'left';
                  ctx.textBaseline = 'middle';
                  ctx.fillStyle = '#FFFFFF';
                }
                if (Difference.isGraphicOrEmbeddedImage(differences) && this.documentType === DocumentTypes.target) {
                  ctx.strokeStyle = showDifferenceBoxes ? '#cd3434' : 'rgba(255, 255, 255, 0.01)'; // transparent but clickable
                  ctx.lineWidth = 1;
                }

                ctx.globalCompositeOperation = 'source-over';
                const colorDiffEntry = differenceLookup.get(boxSettings.refAnnotationId);
                if (colorDiffEntry?.type === DifferenceTypeNames.Color) {
                  ctx.strokeRect(boxSettings.x, boxSettings.y, boxSettings.width, boxSettings.height);
                  const centerX = boxSettings.x + boxSettings.width / 2;
                  const centerY = boxSettings.y + boxSettings.height / 2;
                  ctx.fillStyle = strokeStyle;
                  ctx.fillRect(centerX - 1, centerY - 1, 2, 2);
                  if (showDifferenceBoxes) {
                    const n = ((colorDiffEntry as DifferenceType).differenceIndex ?? 0) + 1;
                    const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
                    const { SELECTED_FILL_STYLE } = PREP_TOOL_ANNOTATION;
                    const zoomLevel = this.instance.Core.documentViewer.getZoomLevel();
                    const currentPageRotation = getPageRotation(this.instance, pageNumber);
                    const pgW = this.instance.Core.documentViewer.getPageWidth(pageNumber);
                    const pgH = this.instance.Core.documentViewer.getPageHeight(pageNumber);
                    drawMarkupLabel(
                      ctx,
                      `Color Point #${n}`,
                      boxSettings.x,
                      boxSettings.y,
                      HEIGHT,
                      GAP,
                      WIDTH_PADDING,
                      SELECTED_FILL_STYLE,
                      zoomLevel,
                      false,
                      boxSettings.width,
                      boxSettings.height,
                      currentPageRotation,
                      pgW,
                      pgH,
                    );
                  }
                } else {
                  ctx.strokeRect(boxSettings.x, boxSettings.y, boxSettings.width, boxSettings.height);
                }
                if (boxSettings.label) {
                  const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
                  const { SELECTED_FILL_STYLE } = PREP_TOOL_ANNOTATION;
                  const zoomLevel = this.instance.Core.documentViewer.getZoomLevel();
                  const labelColor = boxSettings.labelColor || SELECTED_FILL_STYLE;
                  const currentPageRotation = getPageRotation(this.instance, pageNumber);
                  const pgW = this.instance.Core.documentViewer.getPageWidth(pageNumber);
                  const pgH = this.instance.Core.documentViewer.getPageHeight(pageNumber);
                  drawMarkupLabel(
                    ctx,
                    boxSettings.label === 'before' ? 'before' : 'after',
                    boxSettings.x,
                    boxSettings.y,
                    HEIGHT,
                    GAP,
                    WIDTH_PADDING,
                    labelColor,
                    zoomLevel,
                    false,
                    boxSettings.width,
                    boxSettings.height,
                    currentPageRotation,
                    pgW,
                    pgH,
                  );
                }
              }
            }
          });
          ctx.restore(); // Restore canvas state after overlay drawing
        }
      };
      overlayAnnotation.setPageNumber(pageNumber);

      // CRITICAL FIX: Delete ALL high-contrast overlays on this page before creating new ones
      // This prevents overlay accumulation when switching modules, navigating differences, etc.
      // OPTIMIZATION: Check if we already have the correct overlay to avoid unnecessary delete/recreate cycles
      const allPageOverlays = this.getAnnotationList(
        (annot: Core.Annotations.Annotation) =>
          annot.getCustomData('highContrast') === 'true' && annot.PageNumber === pageNumber,
      );

      // Check if existing overlay matches what we're about to create
      // Must check: annotation IDs, visibility settings, AND deletion marker flags
      // because the overlay's draw() function and behavior depends on these
      const isEmbeddedImageWithDeletionMarker = embeddedImageDiff && shouldShowMarker;
      const isRegularDeletionMarker = shouldShowMarker && !embeddedImageDiff;
      const hasEmbeddedImageButNoMarker = embeddedImageDiff && !shouldShowMarker;

      const existingOverlay = allPageOverlays.find((overlay) => {
        const existingIds = overlay.getCustomData(AnnotationCustomData.refAnnotationIds);
        const existingNoDiffBox = overlay.getCustomData(AnnotationCustomData.noDiffBox) === 'true';
        const existingHiddenOverlay = overlay.getCustomData(AnnotationCustomData.hiddenOverlay) === 'true';
        const existingIsDeletion = overlay.getCustomData('isDeletion') === 'true';
        const existingNoMarker = overlay.getCustomData('noMarker') === 'true';
        const existingEmbeddedImageId = overlay.getCustomData(AnnotationCustomData.embeddedImageId);

        // Check if all properties match what we're about to create
        const idsMatch = existingIds === refAnnotationIds.join(',');
        const visibilityMatches = existingNoDiffBox === !showDifferenceBoxes && existingHiddenOverlay === !showOverlay;

        // Check if deletion marker flags match
        let markerFlagsMatch = true;
        if (isEmbeddedImageWithDeletionMarker) {
          markerFlagsMatch = existingIsDeletion && existingEmbeddedImageId === embeddedImageDiff?.id;
        } else if (hasEmbeddedImageButNoMarker) {
          markerFlagsMatch = existingNoMarker === true && existingEmbeddedImageId === embeddedImageDiff?.id;
        } else if (isRegularDeletionMarker) {
          markerFlagsMatch = existingIsDeletion;
        } else {
          // Standard overlay - should NOT have deletion marker flags
          markerFlagsMatch = !existingIsDeletion && !existingNoMarker;
        }

        return idsMatch && visibilityMatches && markerFlagsMatch;
      });

      // Delete and recreate if overlay doesn't exist, doesn't match, or there are multiple overlays
      if (!existingOverlay || allPageOverlays.length > 1) {
        // Delete in batch to minimize canvas redraws
        this.annotationManager.deleteAnnotations(allPageOverlays, { imported: true, force: true });
      } else {
        // Overlay exists and matches perfectly - still need to run refresh/z-ordering logic
        // Don't skip the rest of the function, just skip annotation creation
        const wrapper = PDFManagerFactory.getViewer(this.documentType);
        if (wrapper) {
          PDFAnnotationManager.bringInternalAnnotationsToFront(this.documentType as DocumentType, pageNumber);
          redrawPages(wrapper.instance.Core.documentViewer, [pageNumber]);
        }
        return;
      }

      // Since we deleted all overlays above (or verified none exist), now we always create a fresh one
      if (isEmbeddedImageWithDeletionMarker) {
        // For embedded images with deletion markers
        overlayAnnotation.setCustomData('isDeletion', 'true');
        overlayAnnotation.setCustomData(AnnotationCustomData.deletionMarker, 'true');
        overlayAnnotation.setCustomData(AnnotationCustomData.embeddedImageId, embeddedImageDiff?.id);
        overlayAnnotation.ReadOnly = false;

        this.annotationManager.addAnnotation(overlayAnnotation);
        setTimeout(() => {
          this.annotationManager.selectAnnotation(overlayAnnotation);
        }, 100);
      } else if (hasEmbeddedImageButNoMarker) {
        // For embedded images where THIS document should NOT show deletion marker
        overlayAnnotation.setCustomData(AnnotationCustomData.embeddedImageId, embeddedImageDiff?.id);
        overlayAnnotation.setCustomData('isDeletion', 'false');
        overlayAnnotation.setCustomData('noMarker', 'true');

        this.annotationManager.addAnnotation(overlayAnnotation);
      } else if (isRegularDeletionMarker) {
        // Regular deletion marker
        overlayAnnotation.setCustomData('isDeletion', 'true');
        overlayAnnotation.setCustomData(AnnotationCustomData.deletionMarker, 'true');
        overlayAnnotation.setCustomData('regularDeletion', 'true');
        overlayAnnotation.ReadOnly = false;

        this.annotationManager.addAnnotation(overlayAnnotation);
        setTimeout(() => {
          this.annotationManager.selectAnnotation(overlayAnnotation);
        }, 200);
      } else {
        // Standard overlay (no deletion marker)
        this.annotationManager.addAnnotation(overlayAnnotation);
      }
      const wrapper = PDFManagerFactory.getViewer(this.documentType);
      if (wrapper) {
        PDFAnnotationManager.bringInternalAnnotationsToFront(this.documentType as DocumentType, pageNumber);

        redrawPages(wrapper.instance.Core.documentViewer, [pageNumber]);
      }
    });
  }

  repeatAnnotationHighContrast(annotations: Core.Annotations.Annotation[]) {
    const annotationsByPage: Array<Core.Annotations.Annotation[]> = [];
    const showOverlay = getShowOverlay(store.getState());
    // to identify high contrast annotations when we need to restore them, we identify them by groupId for groups or Id for non-groups

    annotations.forEach((annotation: Core.Annotations.Annotation) => {
      const existingAnnotation = this.getAnnotationById(annotation.Id);
      if (existingAnnotation) {
        // Skip crop and polygon zones - they have their own overlay drawing system
        const isCropZone = existingAnnotation.getCustomData('cropZoneAnnotation') === 'true';
        const isPolygonZone = existingAnnotation.getCustomData('polygonAnnotation') === 'true';
        if (isCropZone || isPolygonZone) {
          return;
        }

        const pageNumber = existingAnnotation?.PageNumber;
        annotationsByPage[pageNumber] = annotationsByPage[pageNumber] || [];
        annotationsByPage[pageNumber]?.push(existingAnnotation);
      }
    });

    annotationsByPage.forEach((pageAnnotations: Core.Annotations.Annotation[]) => {
      const pageNumber = pageAnnotations[0].PageNumber;
      const pageSize = this.page.getPageSize(pageNumber);

      const overlayBoxes = pageAnnotations.map((annotation) => {
        // For repeat annotations, use original bounds if available (excluding annotation label area)
        const originalBoundsData = annotation.getCustomData('originalBounds');
        if (originalBoundsData) {
          const originalBounds = JSON.parse(originalBoundsData);
          // Normalize bounds for negative width/height (mirrored/rotated images)
          const rawWidth = originalBounds.width || 0;
          const rawHeight = originalBounds.height || 0;
          return {
            height: Math.abs(rawHeight),
            width: Math.abs(rawWidth),
            x: rawWidth < 0 ? originalBounds.x + rawWidth : originalBounds.x,
            y: rawHeight < 0 ? originalBounds.y + rawHeight : originalBounds.y,
            refAnnotationId: annotation.Id,
          };
        }

        // Default to current annotation bounds (including annotation label area) just in case
        // Normalize bounds for negative width/height (mirrored/rotated images)
        const rawWidth = annotation.Width || 0;
        const rawHeight = annotation.Height || 0;
        return {
          height: Math.abs(rawHeight),
          width: Math.abs(rawWidth),
          x: rawWidth < 0 ? annotation.X + rawWidth : annotation.X,
          y: rawHeight < 0 ? annotation.Y + rawHeight : annotation.Y,
          refAnnotationId: annotation.Id,
        };
      });

      const refAnnotationIds = pageAnnotations.map((annot) => annot.Id);
      const overlayAnnotation = new (GVAnnotationMixin(this.instance.Core.Annotations.RectangleAnnotation))();
      overlayAnnotation.setCustomData(AnnotationCustomData.highContrast, 'true');
      overlayAnnotation.setCustomData(AnnotationCustomData.refAnnotationIds, refAnnotationIds.join(','));
      overlayAnnotation.NoResize = true;
      overlayAnnotation.ReadOnly = true;
      if (!showOverlay) overlayAnnotation.setCustomData(AnnotationCustomData.hiddenOverlay, 'true');

      overlayAnnotation.draw = (ctx: CanvasRenderingContext2D) => {
        ctx.save(); // Save canvas state BEFORE modifying it
        ctx.globalAlpha = 0.6;
        ctx.fillStyle = showOverlay ? '#404040' : 'rgba(255, 255, 255, 0.01)';
        ctx.fillRect(0, 0, pageSize.width, pageSize.height);

        overlayBoxes.forEach((boxSettings) => {
          if (boxSettings) {
            ctx.clearRect(boxSettings.x, boxSettings.y, boxSettings.width, boxSettings.height);
            ctx.globalAlpha = 1;
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.01)';
            ctx.lineWidth = 1;

            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeRect(boxSettings.x, boxSettings.y, boxSettings.width, boxSettings.height);
          }
        });
        ctx.restore(); // Restore canvas state after overlay drawing
      };
      overlayAnnotation.setPageNumber(pageNumber);

      // CRITICAL FIX: Delete ALL high-contrast overlays on this page before creating new one
      // This prevents overlay accumulation when switching between repeats and differences
      // OPTIMIZATION: Check if we already have the correct overlay
      const allPageOverlays = this.getAnnotationList(
        (annot: Core.Annotations.Annotation) =>
          annot.getCustomData('highContrast') === 'true' && annot.PageNumber === pageNumber,
      );

      // Check if existing overlay matches what we're about to create
      // Must check BOTH annotation IDs AND visibility settings
      const existingOverlay = allPageOverlays.find((overlay) => {
        const existingIds = overlay.getCustomData(AnnotationCustomData.refAnnotationIds);
        const existingHiddenOverlay = overlay.getCustomData(AnnotationCustomData.hiddenOverlay) === 'true';

        return existingIds === refAnnotationIds.join(',') && existingHiddenOverlay === !showOverlay;
      });

      // Delete and recreate if overlay doesn't exist, doesn't match, or there are multiple overlays
      if (!existingOverlay || allPageOverlays.length > 1) {
        this.annotationManager.deleteAnnotations(allPageOverlays, { imported: true, force: true });
      } else {
        // Overlay exists and matches - still need to run refresh/z-ordering logic
        const wrapper = PDFManagerFactory.getViewer(this.documentType);
        if (wrapper) {
          PDFAnnotationManager.bringInternalAnnotationsToFront(this.documentType as DocumentType, pageNumber);

          const repeatMatchIndex = annotations[0].getCustomData(AnnotationCustomData.matchIndex);
          if (this.documentType === DocumentTypes.target) {
            const annotationsList = this.annotationManager.getAnnotationsList();
            const annots = annotationsList.filter(
              (annot) =>
                annot.getCustomData('difference') === 'true' &&
                annot.getCustomData(AnnotationCustomData.matchIndex) === repeatMatchIndex,
            );
            // Bring repeat annotation first, then differences on top so they remain clickable
            PDFAnnotationManager.bringAnnotationsToFront(DocumentTypes.target, [annotations[0], ...annots]);
          }

          redrawPages(wrapper.instance.Core.documentViewer, [pageNumber]);
        }
        return;
      }

      this.annotationManager.addAnnotation(overlayAnnotation);

      const annotationsList = this.annotationManager.getAnnotationsList();

      const wrapper = PDFManagerFactory.getViewer(this.documentType);
      if (wrapper) {
        PDFAnnotationManager.bringInternalAnnotationsToFront(this.documentType as DocumentType, pageNumber);

        const repeatMatchIndex = annotations[0].getCustomData(AnnotationCustomData.matchIndex);
        if (this.documentType === DocumentTypes.target) {
          const annots = annotationsList.filter(
            (annot) =>
              annot.getCustomData('difference') === 'true' &&
              annot.getCustomData(AnnotationCustomData.matchIndex) === repeatMatchIndex,
          );
          // Bring repeat annotation first, then differences on top so they remain clickable
          PDFAnnotationManager.bringAnnotationsToFront(DocumentTypes.target, [annotations[0], ...annots]);
        }

        redrawPages(wrapper.instance.Core.documentViewer, [pageNumber]);
      }
    });
  }

  setCustomData(annotationId: string, key: string, value: string) {
    const annotation = this.annotationManager?.getAnnotationById(annotationId);
    if (annotation) {
      annotation.setCustomData(key, value);
    }
  }

  setAnnotationPageNumber(annotationId: string) {
    const annotation = this.annotationManager?.getAnnotationById(annotationId);
    if (annotation) {
      this.page.setCurrentPageNumber(annotation.PageNumber);
    }
  }

  jumpToAnnotation(annotationId: string) {
    const annotation = this.annotationManager?.getAnnotationById(annotationId);
    if (annotation) {
      this.annotationManager?.jumpToAnnotation(annotation);
    }
  }

  getAnnotationById(annotationId: string): Core.Annotations.Annotation {
    return this.annotationManager.getAnnotationById(annotationId);
  }

  getAnnotationList(filter?: (annotation: Core.Annotations.Annotation) => boolean): Core.Annotations.Annotation[] {
    const annotations = this.annotationManager.getAnnotationsList();
    if (filter) {
      return annotations.filter(filter);
    }
    return annotations || [];
  }

  /**
   * Delete annotations by filter
   */
  deleteAllAnnotationsByFilter(filterFunction: (annotation: Core.Annotations.Annotation) => boolean) {
    const filteredAnnotations = this.getAnnotationList().filter(filterFunction);

    if (filteredAnnotations) {
      this.instance.Core.annotationManager.deleteAnnotations(filteredAnnotations, { imported: true, force: true });
    }
  }

  /**
   * Delete all annotations on excluded pages
   */
  deleteAllExcludedAnnotations() {
    const listOfExcludedPageAnnotations = this.getAnnotationList().filter(
      (annotation: Core.Annotations.Annotation) => annotation.Subject === GVAnnotationType.excludedPage,
    );

    this.instance.Core.annotationManager.deleteAnnotations(listOfExcludedPageAnnotations, {
      imported: true,
      force: true,
    });
  }

  drawExcludeAnnotationByPage(pageNumber: number) {
    const PageWidth = this.instance.Core.documentViewer.getPageWidth(pageNumber);
    const PageHeight = this.instance.Core.documentViewer.getPageHeight(pageNumber);

    const rectangleAnnot = new (GVAnnotationMixin(this.instance.Core.Annotations.RectangleAnnotation))();
    rectangleAnnot.ReadOnly = true;
    rectangleAnnot.Subject = GVAnnotationType.excludedPage;
    const annotationManager = this.instance.Core.documentViewer.getAnnotationManager();
    // overlay the current page with a grey canvas filter
    rectangleAnnot.draw = (ctx: CanvasRenderingContext2D) => {
      ctx.globalAlpha = 0.6;
      ctx.save();
      ctx.fillStyle = '#404040';
      ctx.fillRect(0, 0, PageWidth, PageHeight);

      ctx.restore();
    };
    rectangleAnnot.setPageNumber(pageNumber);
    annotationManager.addAnnotation(rectangleAnnot);
    annotationManager.redrawAnnotation(rectangleAnnot);
  }
}

export default Annotation;
